import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UtilService } from '../../../../shared/services/util.service';
import { AtributoProdutoService } from '../../../../shared/services/apis/produto/atributo-produto.service';
import { AtributoProdutoStruct } from '../../../../shared/services/structs/produto/atributo-produto.struct';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-config-atributos-produtos',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    HeaderComponent,
    EmptyListComponent,
    RouterModule,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-atributos-produtos.component.html',
  styleUrl: './config-atributos-produtos.component.css',
})
export class ConfigAtributosProdutosComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new MatTableDataSource<AtributoProdutoStruct>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'nome', 'campoEquivalente', 'actions'];

  public selection = new SelectionModel<AtributoProdutoStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
    this._dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'campoEquivalente':
          return normalizarValorOrdenacao(item.descricaoCampoEquivalenteAtributo);
        default:
          return normalizarValorOrdenacao((item as any)[property]);
      }
    };
  }

  constructor(
    private formBuilder: FormBuilder,
    private atributoProdutoService: AtributoProdutoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarAtributos();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/atributos-produtos/cadastrar',
      '/configuracoes/cadastros/atributos-produtos/cadastrar/',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarAtributos(reset: boolean = false): void {
    this._isLoading = true;
    const nome = this.model.get('pesquisa')?.value || '';

    this.atributoProdutoService.getAll(nome, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar atributos',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;

        if (reset) {
          this._dataSource.data = response.listaAtributos;
          this.selection.clear();
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaAtributos];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar atributos',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarAtributos();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._dataSource.data = [];
    this.selection.clear();
    this.carregarAtributos(true);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: AtributoProdutoStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const title = 'Confirmar exclusão';
    const description = isBulk
      ? 'Confirma a exclusão dos atributos selecionados?'
      : 'Confirma a exclusão deste atributo?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idAtributo);
          this.atributoProdutoService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  `Erro ao excluir atributos: ${response.errorDescription}`,
                  AlertType.warning
                );
              } else {
                this.alertService.show('Sucesso', 'Atributos excluídos com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir atributos',
                AlertType.error
              );
            },
          });
        } else {
          this.atributoProdutoService.delete(item!.idAtributo).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir atributo',
                  AlertType.error
                );
              } else {
                this.alertService.show('Sucesso', 'Atributo excluído com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir atributo',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
